import React, { useEffect, useState } from "react";
import { Plus, Search, Globe, Building2, User } from "lucide-react";
import api, { apiError } from "../../api";
import { useI18n } from "../../i18n";
import { Button, Card, Input, Field, Select, Textarea, Modal, Badge, Spinner, EmptyState, cx } from "../../components/ui";
import { toast } from "sonner";

const DIFF = { debutant: "beginner", intermediaire: "intermediate", avance: "advanced" };
const LEVEL_ICON = { global: Globe, clinic: Building2, personal: User };

export default function Exercises() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/exercises").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const filtered = (items || []).filter((e) =>
    (e.name_fr + e.name_en + e.name_ar).toLowerCase().includes(q.toLowerCase()) && (!cat || e.category === cat));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div><h1 className="font-head font-bold text-navy text-2xl">{t("exercise_library")}</h1>
          <p className="text-slate-500 text-sm">{(items || []).length} exercices</p></div>
        <Button className="ms-auto" onClick={() => { setEdit(null); setOpen(true); }} data-testid="new-exercise-btn"><Plus size={17} /> {t("new_exercise")}</Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={17} className="absolute start-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="ps-9" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="exercise-search" />
        </div>
        <Select className="max-w-xs" value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">Toutes les catégories</option>
          {cats.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
        </Select>
      </div>

      {!items ? <div className="flex justify-center py-16"><Spinner /></div> :
        filtered.length === 0 ? <EmptyState title={t("no_data")} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((e) => {
            const Icon = LEVEL_ICON[e.sharing_level] || User;
            return (
              <Card key={e.id} className="overflow-hidden group cursor-pointer hover:shadow-card transition animate-fade" onClick={() => { setEdit(e); setOpen(true); }} data-testid={`exercise-card-${e.id}`}>
                <div className="aspect-video bg-slate-100 overflow-hidden">
                  {e.image_url ? <img src={e.image_url} alt={e.alt_text} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" /> :
                    <div className="w-full h-full flex items-center justify-center text-slate-300">SpinaGo</div>}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-1.5 mb-1"><Icon size={13} className="text-teal" /><span className="text-[10px] uppercase tracking-wide text-slate-400">{e.sharing_level}</span></div>
                  <h3 className="font-head font-semibold text-navy leading-tight line-clamp-1">{e.name_fr}</h3>
                  <p className="text-xs text-slate-400 mt-0.5 line-clamp-1" dir="rtl">{e.name_ar}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <Badge tone="navy">{(e.category || "").replace(/_/g, " ")}</Badge>
                    <Badge tone="teal">{t(DIFF[e.difficulty] || "beginner")}</Badge>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <ExerciseModal open={open} onClose={() => setOpen(false)} onDone={load} edit={edit} cats={cats} t={t} />
    </div>
  );
}

function ExerciseModal({ open, onClose, onDone, edit, cats, t }) {
  const base = { name_fr: "", name_ar: "", name_en: "", category: "", body_region: "", objective: "",
    difficulty: "debutant", start_position: "", description: "", instructions: "", mistakes: "",
    precautions: "", default_sets: 3, default_reps: 10, default_duration: "30 s", default_frequency: "1x/jour",
    default_rest: "30 s", equipment: "", image_url: "", video_url: "", alt_text: "", sharing_level: "personal", status: "published" };
  const [f, setF] = useState(base);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { setF(edit ? { ...base, ...edit } : base); }, [edit, open]);

  const submit = async () => {
    if (!f.name_fr) { toast.error("Nom français requis"); return; }
    setBusy(true);
    try {
      if (edit) await api.put(`/exercises/${edit.id}`, f); else await api.post("/exercises", f);
      toast.success(edit ? "Exercice modifié" : "Exercice créé"); onClose(); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Modal open={open} onClose={onClose} title={edit ? t("edit") : t("new_exercise")} size="lg">
      <div className="space-y-3">
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Nom (FR)"><Input data-testid="ex-name-fr" value={f.name_fr} onChange={(e) => set("name_fr", e.target.value)} /></Field>
          <Field label="Nom (AR)"><Input dir="rtl" value={f.name_ar} onChange={(e) => set("name_ar", e.target.value)} /></Field>
          <Field label="Nom (EN)"><Input value={f.name_en} onChange={(e) => set("name_en", e.target.value)} /></Field>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label={t("category")}><Select value={f.category} onChange={(e) => set("category", e.target.value)}>
            <option value="">—</option>{cats.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}</Select></Field>
          <Field label="Région"><Select value={f.body_region} onChange={(e) => set("body_region", e.target.value)}>
            <option value="">—</option><option value="cervicale">Cervicale</option><option value="thoracique">Thoracique</option>
            <option value="lombaire">Lombaire</option><option value="hanche">Hanche</option><option value="autre">Autre</option></Select></Field>
          <Field label={t("difficulty")}><Select value={f.difficulty} onChange={(e) => set("difficulty", e.target.value)}>
            <option value="debutant">{t("beginner")}</option><option value="intermediaire">{t("intermediate")}</option><option value="avance">{t("advanced")}</option></Select></Field>
        </div>
        <Field label={t("objective")}><Input value={f.objective} onChange={(e) => set("objective", e.target.value)} /></Field>
        <Field label={t("start_position")}><Input value={f.start_position} onChange={(e) => set("start_position", e.target.value)} /></Field>
        <Field label={t("instructions")}><Textarea rows={2} value={f.instructions} onChange={(e) => set("instructions", e.target.value)} /></Field>
        <Field label={t("mistakes")}><Textarea rows={2} value={f.mistakes} onChange={(e) => set("mistakes", e.target.value)} /></Field>
        <div className="grid sm:grid-cols-5 gap-2">
          <Field label={t("sets")}><Input type="number" value={f.default_sets} onChange={(e) => set("default_sets", Number(e.target.value))} /></Field>
          <Field label={t("reps")}><Input type="number" value={f.default_reps} onChange={(e) => set("default_reps", Number(e.target.value))} /></Field>
          <Field label={t("duration")}><Input value={f.default_duration} onChange={(e) => set("default_duration", e.target.value)} /></Field>
          <Field label={t("frequency")}><Input value={f.default_frequency} onChange={(e) => set("default_frequency", e.target.value)} /></Field>
          <Field label={t("rest")}><Input value={f.default_rest} onChange={(e) => set("default_rest", e.target.value)} /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Image (URL)"><Input data-testid="ex-image" value={f.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://…" /></Field>
          <Field label="Vidéo MP4 (URL)"><Input value={f.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://….mp4" /></Field>
        </div>
        <Field label={`${t("sharing")}`}><Select value={f.sharing_level} onChange={(e) => set("sharing_level", e.target.value)} data-testid="ex-sharing">
          <option value="personal">Personnel</option><option value="clinic">Cabinet</option><option value="global">Global SpinaTech</option></Select></Field>
        <p className="text-xs text-slate-400">Recommandation médias : image ≤ 1080p, vidéo MP4 H.264 compressée. Aucun transcodage côté serveur.</p>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={submit} disabled={busy} data-testid="ex-submit">{busy ? t("loading") : t("save")}</Button>
        </div>
      </div>
    </Modal>
  );
}
